import { useState, useRef, useEffect } from "react";
import { useNavigationHistory } from "../../contexts/NavigationHistoryContext";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Tooltip } from "@mantine/core";
import { IconArrowLeft } from "@tabler/icons-react";
import "./BackButton.css";

export function BackButton() {
  const { history, canGoBack, goBack, getPreviousUrl } = useNavigationHistory();
  const navigate = useNavigate();
  const location = useLocation();
  const [isAnimating, setIsAnimating] = useState(false);
  const [canNavigateBack, setCanNavigateBack] = useState(canGoBack());
  const buttonRef = useRef(null);

  // Update canNavigateBack whenever history changes
  useEffect(() => {
    setCanNavigateBack(canGoBack());
  }, [history, canGoBack]);

  // Calculate steps back based on our tracked history
  // window.history.length is not reliable for this purpose
  // We'll use our own history array which is limited to 10 entries
  const stepsBack = canNavigateBack ? Math.max(0, history.length - 1) : 0;

  // Function to create ripple effect
  const createRipple = (event) => {
    if (!buttonRef.current || !canNavigateBack) return;

    const button = buttonRef.current;
    const circle = document.createElement("span");
    const diameter = Math.max(button.clientWidth, button.clientHeight);
    const radius = diameter / 2;

    // Get button position
    const rect = button.getBoundingClientRect();

    circle.style.width = circle.style.height = `${diameter}px`;
    circle.style.left = `${event.clientX - rect.left - radius}px`;
    circle.style.top = `${event.clientY - rect.top - radius}px`;
    circle.classList.add("back-button-ripple");

    // Remove existing ripples
    const ripple = button.getElementsByClassName("back-button-ripple");
    if (ripple.length) {
      ripple[0].remove();
    }

    button.appendChild(circle);

    // Remove the ripple after animation completes
    setTimeout(() => {
      if (circle) {
        circle.remove();
      }
    }, 600);
  };

  const handleGoBack = (event) => {
    if (!canNavigateBack || isAnimating) {
      return;
    }

    // Create ripple effect
    createRipple(event);

    setIsAnimating(true);

    // Add a small delay for the animation
    setTimeout(() => {
      // Get the previous URL from our history
      const previousUrl = getPreviousUrl();

      // Check if we're on the same page but with different params
      const currentPathname = location.pathname;
      const previousPathname = previousUrl.split("?")[0];

      // Update the history state in the context
      goBack();

      // Navigate to the previous URL using React Router (client-side navigation)
      navigate(previousUrl);

      setIsAnimating(false);
    }, 300);
  };

  // Clean up any ripples when component unmounts
  useEffect(() => {
    return () => {
      if (buttonRef.current) {
        const ripples = buttonRef.current.getElementsByClassName("back-button-ripple");
        while (ripples.length) {
          ripples[0].remove();
        }
      }
    };
  }, []);

  return (
    <div className="back-button-container">
      <Tooltip label={canNavigateBack ? "Go back to previous page" : "No previous page"} position="bottom" withArrow>
        <Button
          ref={buttonRef}
          onClick={handleGoBack}
          variant={canNavigateBack ? "filled" : "subtle"}
          color={canNavigateBack ? "accent.5" : "gray.5"}
          leftSection={<IconArrowLeft size={20} />}
          disabled={!canNavigateBack || isAnimating}
          aria-label="Go back to previous page"
          size="sm"
          radius="md"
          className={isAnimating ? "back-button-animating" : ""}
          styles={(theme) => ({
            root: {
              padding: "8px 16px",
              transition: theme.other.transition.default,
              boxShadow: canNavigateBack ? theme.shadows.sm : "none",
              position: "relative",
              overflow: "hidden", // For ripple effect
              "&:hover": canNavigateBack
                ? {
                    backgroundColor: theme.colors.accent[6],
                    transform: "translateY(-2px)",
                    boxShadow: theme.shadows.md,
                  }
                : {},
              "&[dataDisabled]": {
                opacity: 0.6,
                backgroundColor: theme.colors.gray[1],
                color: theme.colors.gray[6],
                cursor: "not-allowed",
                boxShadow: "none",
              },
            },
            leftIcon: {
              marginRight: theme.spacing.xs,
            },
          })}
        >
          Back
        </Button>
      </Tooltip>

      {/* Badge removed as requested */}
    </div>
  );
}

export default BackButton;
